import type { AxisSelection } from '@atlas/types'
import type {
	Campaign,
	CampaignFile,
	CampaignStatus,
	CreateCampaignInput,
	UpdateCampaignInput,
} from '@/domain/campaign.types'
import { apiClient } from './api.client'

/** Campaign Core API client — thin `apiClient` wrappers, no business logic, no error translation. */
const unwrap = <T>(data: { data: T }): T => data.data

/**
 * `GET /campaigns` query shape. `status` is joined into one comma-separated
 * `filters[status]` parameter (never Axios's default array serialization,
 * which would emit `filters[status][]=…`); each `classification` axis becomes
 * its own `filters[<axisCode>]` parameter.
 */
export interface CampaignListParams {
	readonly status?: readonly CampaignStatus[]
	readonly classification?: AxisSelection
	readonly page?: number
	readonly limit?: number
}

function listQueryParams(params: CampaignListParams): Record<string, string | number> {
	const query: Record<string, string | number> = {}
	if (params.status && params.status.length > 0) query['filters[status]'] = params.status.join(',')
	if (params.classification) {
		for (const [axisCode, values] of Object.entries(params.classification)) {
			if (values && values.length > 0) query[`filters[${axisCode}]`] = values.join(',')
		}
	}
	if (params.page !== undefined) query.page = params.page
	if (params.limit !== undefined) query.limit = params.limit
	return query
}

export const campaignService = {
	listCampaigns: (params: CampaignListParams = {}): Promise<Campaign[]> =>
		apiClient.get<{ data: Campaign[] }>('/campaigns', { params: listQueryParams(params) }).then((r) => unwrap(r.data)),

	createCampaign: (input: CreateCampaignInput): Promise<Campaign> =>
		apiClient.post<{ data: Campaign }>('/campaigns', input).then((r) => unwrap(r.data)),

	getCampaign: (campaignId: string): Promise<Campaign> =>
		apiClient.get<{ data: Campaign }>(`/campaigns/${campaignId}`).then((r) => unwrap(r.data)),

	updateCampaign: (campaignId: string, input: UpdateCampaignInput): Promise<Campaign> =>
		apiClient.patch<{ data: Campaign }>(`/campaigns/${campaignId}`, input).then((r) => unwrap(r.data)),

	// 204, no body — resolves void, never attempts to unwrap a body.
	deleteCampaign: (campaignId: string): Promise<void> =>
		apiClient.delete<void>(`/campaigns/${campaignId}`).then(() => undefined),

	transitionCampaign: (campaignId: string, to: CampaignStatus): Promise<Campaign> =>
		apiClient.post<{ data: Campaign }>(`/campaigns/${campaignId}/transition`, { to }).then((r) => unwrap(r.data)),

	getAvailableActions: (campaignId: string): Promise<readonly CampaignStatus[]> =>
		apiClient
			.get<{ data: readonly CampaignStatus[] }>(`/campaigns/${campaignId}/available-actions`)
			.then((r) => unwrap(r.data)),

	listAssociatedFiles: (campaignId: string, page: number, limit: number): Promise<CampaignFile[]> =>
		apiClient
			.get<{ data: CampaignFile[] }>(`/campaigns/${campaignId}/files`, { params: { page, limit } })
			.then((r) => unwrap(r.data)),

	// 204, no body — resolves void, never attempts to unwrap a body (mirrors deleteCampaign).
	associateFiles: (campaignId: string, fileIds: readonly string[]): Promise<void> =>
		apiClient.post<void>(`/campaigns/${campaignId}/files`, { fileIds }).then(() => undefined),

	// 204, no body — mirrors associateFiles exactly.
	associateMessages: (campaignId: string, messageIds: readonly string[]): Promise<void> =>
		apiClient.post<void>(`/campaigns/${campaignId}/messages`, { messageIds }).then(() => undefined),
}
